import { Injectable, inject, DestroyRef, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { filter, fromEvent, interval, merge } from 'rxjs';

import { environment } from '../../environments/environment';

const RELOAD_FLAG = 'sw-update-reload-attempted';
const UNREGISTER_RELOAD_FLAG = 'sw-disabled-unregistered-reload';
const CHECK_INTERVAL_MS = 60_000;

/**
 * Detects new app versions via the Angular service worker and prompts the user
 * to reload. Reload runs only after explicit confirmation (except unrecoverable SW state).
 */
@Injectable({ providedIn: 'root' })
export class AppUpdateService {
  private readonly swUpdate = inject(SwUpdate);
  private readonly destroyRef = inject(DestroyRef);

  private hasPrompted = false;

  /** Drives the branded update modal (first prompt). */
  readonly showUpdateModal = signal(false);

  /**
   * True after the user chooses "Later" on the update modal.
   * Drives the sticky top banner so they can still reload later.
   */
  readonly updateAvailable = signal(false);

  init(): void {
    if (!environment.enableServiceWorker || !this.swUpdate.isEnabled) {
      void this.unregisterStaleWorkers();
      return;
    }

    sessionStorage.removeItem(RELOAD_FLAG);
    sessionStorage.removeItem(UNREGISTER_RELOAD_FLAG);

    this.swUpdate.versionUpdates
      .pipe(
        filter((event): event is VersionReadyEvent => event.type === 'VERSION_READY'),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        if (!this.hasPrompted) {
          this.hasPrompted = true;
          this.promptForUpdate();
        } else {
          this.updateAvailable.set(true);
        }
      });

    this.swUpdate.unrecoverable.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.hardReload();
    });

    merge(
      interval(CHECK_INTERVAL_MS),
      fromEvent(document, 'visibilitychange').pipe(filter(() => document.visibilityState === 'visible'))
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        void this.swUpdate.checkForUpdate();
      });

    void this.swUpdate.checkForUpdate();
  }

  /** Activate the waiting service-worker version and hard-reload the page. */
  applyUpdate(): void {
    if (sessionStorage.getItem(RELOAD_FLAG)) {
      return;
    }

    sessionStorage.setItem(RELOAD_FLAG, '1');
    if (!this.swUpdate.isEnabled) {
      this.hardReload();
      return;
    }
    void this.swUpdate.activateUpdate().then(
      () => this.hardReload(),
      () => this.hardReload()
    );
  }

  dismissBanner(): void {
    this.updateAvailable.set(false);
  }

  /** Preview the update modal locally (Ctrl+Shift+U). */
  previewUpdateModal(): void {
    this.updateAvailable.set(false);
    this.showUpdateModal.set(true);
  }

  resolveUpdateModal(confirmed: boolean): void {
    this.showUpdateModal.set(false);
    if (confirmed) {
      this.applyUpdate();
      return;
    }
    // Keep a quiet banner so they can reload without the modal again.
    this.updateAvailable.set(true);
  }

  /**
   * `enabled: false` only skips *new* registration. An older ngsw worker on
   * localhost keeps intercepting XHR until it is unregistered.
   */
  private async unregisterStaleWorkers(): Promise<void> {
    if (typeof navigator === 'undefined' || !navigator.serviceWorker) {
      return;
    }

    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map((registration) => registration.unregister()));

    if (typeof caches !== 'undefined') {
      const keys = await caches.keys();
      await Promise.all(
        keys
          .filter((key) => key.startsWith('ngsw:') || key.includes('ngsw'))
          .map((key) => caches.delete(key))
      );
    }

    if (registrations.length > 0 && !sessionStorage.getItem(UNREGISTER_RELOAD_FLAG)) {
      sessionStorage.setItem(UNREGISTER_RELOAD_FLAG, '1');
      document.location.reload();
    }
  }

  private promptForUpdate(): void {
    this.updateAvailable.set(false);
    this.showUpdateModal.set(true);
  }

  private hardReload(): void {
    document.location.reload();
  }
}
